import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle, Clock, Truck, Paintbrush, Sparkles, PartyPopper,
  Users, MapPin, Navigation, Phone, Loader2, RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const STEP_ICONS = {
  confirmed: CheckCircle,
  team_assigned: Users,
  on_the_way: Truck,
  setup_started: Paintbrush,
  final_touches: Sparkles,
  party_ready: PartyPopper,
};

const STEP_COLORS = {
  confirmed: "#10B981",
  team_assigned: "#3B82F6",
  on_the_way: "#F97316",
  setup_started: "#7C3AED",
  final_touches: "#E83E8C",
  party_ready: "#DC283C",
};

const LivePartyTracker = ({ bookingId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/party-tracker/status/${bookingId}`);
      if (r.ok) {
        setData(await r.json());
        setError("");
      } else {
        setError("Could not load tracking info");
      }
    } catch { setError("Connection error"); }
    setLoading(false);
  }, [bookingId]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="animate-spin text-[#DC283C]" size={28} />
    </div>
  );

  if (error || !data) return (
    <div className="text-center py-8">
      <p className="text-sm text-[#94a3b8]">{error || "No tracking data available yet"}</p>
    </div>
  );

  const currentColor = STEP_COLORS[data.current_step] || "#DC283C";

  return (
    <div className="space-y-5" data-testid="live-party-tracker">
      <style>{`
        @keyframes pulseStep { 0%, 100% { box-shadow: 0 0 0 0 ${currentColor}40; } 50% { box-shadow: 0 0 0 8px ${currentColor}00; } }
        @keyframes slideRight { from { width: 0; } to { width: 100%; } }
        .pulse-step { animation: pulseStep 2s ease-in-out infinite; }
      `}</style>

      {/* Progress Header */}
      <div className="bg-gradient-to-r from-[#0A0E0F] to-[#1E1B4B] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] text-white/40 font-medium uppercase tracking-wider">Live Party Status</p>
            <h3 className="text-lg font-bold mt-0.5">{data.package} — {data.occasion}</h3>
            <p className="text-xs text-white/50 mt-0.5">{data.date} | {data.setup_time}</p>
          </div>
          <button onClick={fetchStatus} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-testid="refresh-tracker">
            <RefreshCw size={14} className="text-white/60" />
          </button>
        </div>
        {/* Progress bar */}
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-1000 ease-out" 
            style={{ width: `${data.progress_pct}%`, background: `linear-gradient(90deg, #10B981, ${currentColor})` }} />
        </div>
        <p className="text-right text-[10px] text-white/40 mt-1">{data.progress_pct}% complete</p>
      </div>

      {/* Status Steps */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="relative">
          {(data.steps || []).map((step, i) => {
            const Icon = STEP_ICONS[step.id] || CheckCircle;
            const color = STEP_COLORS[step.id] || "#94a3b8";
            const isCompleted = step.completed;
            const isCurrent = step.is_current;
            const isLast = i === data.steps.length - 1;

            return (
              <div key={step.id} className="flex gap-4 relative" data-testid={`tracker-step-${step.id}`}>
                {/* Vertical line */}
                {!isLast && (
                  <div className="absolute left-[19px] top-10 bottom-0 w-0.5"
                    style={{ background: isCompleted ? color : "#E5E7EB" }} />
                )}

                {/* Step circle */}
                <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                  isCurrent ? "pulse-step" : ""
                }`}
                  style={{
                    background: isCompleted ? color : "#F3F4F6",
                    border: isCurrent ? `3px solid ${color}` : "none",
                  }}>
                  <Icon size={18} className={isCompleted ? "text-white" : "text-gray-400"} />
                </div>

                {/* Step content */}
                <div className={`flex-1 pb-6 ${isLast ? "pb-0" : ""}`}>
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-bold ${isCompleted ? "text-[#1E1B4B]" : "text-gray-400"}`}>
                      {step.label}
                    </p>
                    {isCurrent && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold text-white"
                        style={{ background: color }}>
                        CURRENT
                      </span>
                    )}
                  </div>
                  {step.timestamp && (
                    <p className="text-[10px] text-[#94a3b8] mt-0.5">
                      {new Date(step.timestamp).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  )}
                  {isCurrent && step.id === "on_the_way" && data.eta_minutes && (
                    <p className="text-xs text-[#F97316] font-medium mt-1 flex items-center gap-1">
                      <Clock size={12} /> ETA: ~{data.eta_minutes} minutes
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Decorator Info + Live Location */}
      {data.decorator && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h4 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2">
            <Users size={14} className="text-[#3B82F6]" /> Your Decorator
          </h4>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
              <Users size={20} className="text-[#3B82F6]" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#1E1B4B]">{data.decorator.name}</p>
              {data.decorator.phone && (
                <a href={`tel:${data.decorator.phone}`} className="flex items-center gap-1 text-xs text-[#3B82F6] hover:underline">
                  <Phone size={10} /> Call Decorator
                </a>
              )}
            </div>
            {data.current_step === "on_the_way" && (
              <div className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F97316]/10 text-[#F97316]">
                <Navigation size={12} className="animate-pulse" />
                <span className="text-[10px] font-bold">En Route</span>
              </div>
            )}
          </div>

          {/* Live Map */}
          {data.decorator.lat && data.decorator.lng && MAPS_KEY && (
            <div className="rounded-xl overflow-hidden border border-gray-200" data-testid="decorator-live-map">
              <img
                src={`https://maps.googleapis.com/maps/api/staticmap?center=${data.decorator.lat},${data.decorator.lng}&zoom=14&size=600x200&markers=color:red|${data.decorator.lat},${data.decorator.lng}&key=${MAPS_KEY}`}
                alt="Decorator location"
                className="w-full h-[180px] object-cover"
              />
              <div className="p-2 bg-gray-50 flex items-center justify-between">
                <span className="text-[10px] text-[#94a3b8] flex items-center gap-1">
                  <MapPin size={10} /> Live location
                </span>
                {data.decorator.location_updated && (
                  <span className="text-[9px] text-[#94a3b8]">
                    Updated: {new Date(data.decorator.location_updated).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notes / Updates */}
      {data.notes?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h4 className="text-sm font-bold text-[#1E1B4B] mb-3">Updates</h4>
          <div className="space-y-2">
            {data.notes.map((n, i) => (
              <div key={`${n.timestamp || i}-${(n.text || "").slice(0, 16)}`} className="flex gap-2 text-xs">
                <span className="text-[#94a3b8] flex-shrink-0 w-16">
                  {new Date(n.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-[#475569]">{n.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LivePartyTracker;
